import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMessageFeed } from "../MessageFeedContext";
import RealTimeGraph from "../components/Devices/RealTimeGraph";
import IconWithValue from "../components/Devices/IconWithValue";
import Card from "../components/UI/Card";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import axios from "axios";
import {
  FaWater,
  FaBatteryEmpty,
  FaBatteryQuarter,
  FaBatteryHalf,
  FaBatteryThreeQuarters,
  FaBatteryFull,
  FaTemperatureHigh,
  FaFlask,
  FaCloud,
  FaTint,
  FaLeaf,
  FaWind,
  FaArrowsAltV,
  FaBiohazard,
  FaWaveSquare,
} from "react-icons/fa";
import DashboardIcon from "../components/UI/DashboardIcon";
import { TitleContext } from "../components/Context";
import SearchIcon from "@mui/icons-material/Search";

const contexts = [
  { id: "sohma_la_paloma_mareografica_context", label: "SOHMA LA PALOMA", color: "#6C4AB6" },
  { id: "sohma_punta_lobos_mareografica_context", label: "SOHMA PUNTA LOBOS", color: "#8E24AA" },
  { id: "anp_muelle_fluvial_mareografo_context", label: "ANP MUELLE FLUVIAL", color: "#0B7285" },
  { id: "anp_colonia_mareografo_context", label: "ANP COLONIA", color: "#0D9488" },
  { id: "anp_fray_bentos_mareografo_context", label: "ANP FRAY BENTOS", color: "#0F766E" },
  { id: "anp_isla_de_flores_mareografo_context", label: "ANP ISLA DE FLORES", color: "#1D4ED8" },
  { id: "anp_la_paloma_mareografo_context", label: "ANP LA PALOMA", color: "#2563EB" },
  { id: "anp_nueva_palmira_mareografo_context", label: "ANP NUEVA PALMIRA", color: "#0891B2" },
  { id: "anp_paysandu_mareografo_context", label: "ANP PAYSANDÚ", color: "#06B6D4" },
  { id: "montevideo_boya_context", label: "BOYA MONTEVIDEO", color: "#1E88E5" },
  { id: "simcosta_rs4_context", label: "SIMCOSTA RS-4 (RIO GRANDE)", color: "#EF476F" },
  { id: "flowers_island_exo1_context", label: "ISLA DE FLORES EXO1", color: "#C2185B" },
  { id: "playa_mvd_pocitos_context", label: "PLAYA POCITOS", color: "#5E60CE" },
  { id: "playa_mvd_buceo_context", label: "PLAYA BUCEO", color: "#4361EE" },
  { id: "playa_mvd_ramirez_context", label: "PLAYA RAMÍREZ", color: "#4CC9F0" },
  { id: "playa_mvd_carrasco_context", label: "PLAYA CARRASCO", color: "#3A0CA3" },
  { id: "playa_mvd_malvin_context", label: "PLAYA MALVÍN", color: "#489F6F" },
  { id: "playa_mvd_arroyo_carrasco_context", label: "ARROYO CARRASCO", color: "#2D6A4F" },
  { id: "playa_mvd_arroyo_molino_context", label: "ARROYO MOLINO", color: "#40916C" },
  { id: "playa_mvd_de_los_ingleses_context", label: "PLAYA DE LOS INGLESES", color: "#74C69D" },
  { id: "playa_mvd_de_la_mulata_context", label: "PLAYA DE LA MULATA", color: "#95D5B2" },
  { id: "playa_mvd_verde_context", label: "PLAYA VERDE", color: "#B5E48C" },
  { id: "playa_mvd_honda_context", label: "PLAYA HONDA", color: "#D9ED92" },
  { id: "playa_mvd_miramar_context", label: "PLAYA MIRAMAR", color: "#F77F00" },
  { id: "playa_mvd_brava_context", label: "PLAYA BRAVA", color: "#F48C06" },
  { id: "playa_mvd_de_los_cilindros_context", label: "PLAYA DE LOS CILINDROS", color: "#FAA307" },
  { id: "playa_mvd_pajas_blancas_context", label: "PLAYA PAJAS BLANCAS", color: "#FFB703" },
  { id: "playa_mvd_punta_yeguas_context", label: "PLAYA PUNTA YEGUAS", color: "#E85D04" },
  { id: "playa_mvd_santa_catalina_context", label: "PLAYA SANTA CATALINA", color: "#DC2F02" },
  { id: "playa_mvd_la_colorada_context", label: "PLAYA LA COLORADA", color: "#9D0208" },
  { id: "playa_mvd_punta_espinillo_context", label: "PLAYA PUNTA ESPINILLO", color: "#6A040F" },
  { id: "playa_mvd_zabala_context", label: "PLAYA ZABALA", color: "#7209B7" },
  { id: "playa_mvd_del_cerro_context", label: "PLAYA DEL CERRO", color: "#560BAD" },
  { id: "playa_mvd_del_nacional_o_frigonal_context", label: "PLAYA DEL NACIONAL", color: "#277DA1" },
];

const formatValue = (value: any, decimals = 2): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return null;
  }
  return parseFloat(num.toFixed(decimals));
};

const getBatteryIconByVoltage = (voltaje: number | null) => {
  if (voltaje === null || voltaje === undefined) {
    return <FaBatteryEmpty size={50} color="#9CA3AF" />;
  }
  if (voltaje < 11.5) return <FaBatteryEmpty size={50} color="red" />;
  if (voltaje < 12) return <FaBatteryQuarter size={50} color="orange" />;
  if (voltaje < 12.5) return <FaBatteryHalf size={50} color="black" />;
  if (voltaje < 13) return <FaBatteryThreeQuarters size={50} color="black" />;
  return <FaBatteryFull size={50} color="green" />;
};

const DevicesPage = () => {
  const { setTitle } = useContext(TitleContext);
  const lastFetchedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    setTitle("Monitoreo en tiempo real");
  }, [setTitle]);
  const { getFeed, prependMessages } = useMessageFeed();
  const [onlyFresh, setOnlyFresh] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Si un feed está vacío, intenta traer la última medición por REST y la inyecta
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_HOST ?? "";
    contexts.forEach(async (ctx) => {
      const feed = getFeed(ctx.id);
      if ((feed?.length ?? 0) > 0 || lastFetchedRef.current.has(ctx.id)) {
        return;
      }
      try {
        lastFetchedRef.current.add(ctx.id);
        const resp = await axios.get(`${baseUrl}/api/v1/contexts/${ctx.id}/last`);
        const data = resp.data?.data || [];
        const mapped = (data as any[]).map((m) => ({
          ...m,
          voltaje_device: m.voltaje_device ?? m.voltajeDevice,
          temperatura_device: m.temperatura_device ?? m.temperaturaDevice,
          timestamp: m.recvTime || m.timestamp,
          contextName: ctx.id,
        }));
        mapped.sort((a, b) => {
          const ta = new Date(a.timestamp || "").getTime();
          const tb = new Date(b.timestamp || "").getTime();
          return ta - tb;
        });
        if (mapped.length) {
          prependMessages(ctx.id, mapped);
        }
      } catch (err) {
        console.error("Error fetching last measurement for devices dashboard", err);
      }
    });
  }, [getFeed, prependMessages]);

  const hasActualData = (ctxId: string) => {
    const msgs = getFeed(ctxId);
    if (!msgs || msgs.length === 0) return false;

    const getFirstNonNull = (keys: string[]) => {
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m: any = msgs[i];
        if (!m) continue;
        for (const k of keys) {
          const v = m[k];
          if (v !== null && v !== undefined) return true;
        }
      }
      return false;
    };

    if (ctxId === "simcosta_rs4_context") {
      return getFirstNonNull([
        "temperatura",
        "temperatura_agua",
        "temperatura_nasa",
        "salinidad",
        "clorofila",
        "turbidez",
        "ola_altura_significativa",
        "ola_altura_maxima",
        "ola_periodo_pico",
      ]);
    }
    if (ctxId === "montevideo_boya_context") {
      return getFirstNonNull(["temperatura_agua", "temperatura_nasa", "velocidad_viento", "ola_altura_significativa"]);
    }
    if (ctxId === "sohma_la_paloma_mareografica_context") {
      return getFirstNonNull(["temperatura_agua", "temperatura_nasa", "nivel_mar"]);
    }
    if (ctxId === "sohma_punta_lobos_mareografica_context") {
      return getFirstNonNull(["temperatura_agua", "nivel_mar"]);
    }
    if (ctxId.startsWith("anp_") && ctxId.includes("mareografo_context")) {
      return getFirstNonNull(["temperatura_agua", "temperatura_nasa", "nivel_mar"]);
    }
    if (ctxId.startsWith("playa_mvd_")) {
      return getFirstNonNull(["temperatura_agua", "temperatura_nasa", "salinidad"]);
    }

    return getFirstNonNull([
      "temperatura",
      "temperatura_agua",
      "temperatura_nasa",
      "salinidad",
      "nivel_mar",
      "conductividad",
    ]);
  };

  const filteredContexts = useMemo(
    () =>
      contexts.filter((ctx) => {
        const matches = ctx.label.toLowerCase().includes(searchTerm.trim().toLowerCase());
        if (!matches) return false;
        if (onlyFresh && !hasActualData(ctx.id)) return false;
        return true;
      }),
    [searchTerm, onlyFresh, getFeed]
  );

  const theme = useTheme();
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isPortraitLayout = isPortrait || isSmallScreen;
  const chartGridProps = {
    xs: isPortraitLayout ? 12 : 9,
    sx: isPortraitLayout ? { order: 1 } : undefined,
  };
  const cardGridProps = {
    xs: isPortraitLayout ? 12 : 3,
    sx: isPortraitLayout ? { order: 2, mt: 2 } : undefined,
  };

  const getLatestValue = (msgs: any[], key: string) => {
    if (msgs.length === 0) {
      return null;
    }
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m: any = msgs[i];
      if (!m) continue;
      const value = m[key];
      if (value !== null && value !== undefined) {
        return value;
      }
    }
    return null;
  };

  const getLatestRecvTimeFor = (msgs: any[], key: string): string | undefined => {
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m: any = msgs[i];
      if (m && m[key] !== null && m[key] !== undefined) {
        return (m.recvTime as string | undefined) || (m.timestamp as string | undefined);
      }
    }
    return undefined;
  };

  const renderNasaTemperature = (ctx: typeof contexts[number], msgs: any[]) => {
    const lastTempNasa = getLatestValue(msgs, "temperatura_nasa");
    const temperaturaNasa = formatValue(lastTempNasa, 2);
    if (temperaturaNasa === null) {
      return null;
    }
    return (
      <>
        <Grid item {...chartGridProps}>
          <RealTimeGraph
            title="EVOLUCIÓN DE TEMPERATURA DEL AGUA (NASA)"
            label="Temperatura NASA"
            borderColor="#00ACC1"
            backgroundColor="#00ACC1"
            messageFeed={msgs}
            metricKey="temperatura_nasa"
            headerColor={ctx.color}
            contextId={ctx.id}
            yAxisRange={{ min: 5, max: 30 }}
          />
        </Grid>
        <Grid item {...cardGridProps}>
          <IconWithValue
            title="ACTUAL NASA"
            value={temperaturaNasa}
            unit="°C"
            icon={<FaTemperatureHigh size={50} />}
            headerColor={ctx.color}
            recvTime={getLatestRecvTimeFor(msgs, "temperatura_nasa")}
          />
        </Grid>
      </>
    );
  };

  const renderSimcostaContext = (ctx: typeof contexts[number]) => {
    const msgs = getFeed(ctx.id);
    const lastTemp = getLatestValue(msgs, "temperatura");
    const lastSal = getLatestValue(msgs, "salinidad");
    const lastClorofila = getLatestValue(msgs, "clorofila");
    const lastTurbidez = getLatestValue(msgs, "turbidez");
    const lastOlaSig = getLatestValue(msgs, "ola_altura_significativa");
    const lastOlaMax = getLatestValue(msgs, "ola_altura_maxima");
    const lastOlaPeriodo = getLatestValue(msgs, "ola_periodo_pico");
    const temperatura = formatValue(lastTemp, 2);
    const salinidad = formatValue(lastSal, 2);
    const clorofila = formatValue(lastClorofila, 2);
    const turbidez = formatValue(lastTurbidez, 2);
    const olaSig = formatValue(lastOlaSig, 2);
    const olaMax = formatValue(lastOlaMax, 2);
    const olaPeriodo = formatValue(lastOlaPeriodo, 2);

    return (
      <Card key={ctx.id} title={`MONITOREO - ${ctx.label}`} headerColor={ctx.color}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <DashboardIcon title="MONITOREO" />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE TEMPERATURA"
              label="Temperatura"
              borderColor="#18265a"
              backgroundColor="#18265a"
              messageFeed={msgs}
              metricKey="temperatura"
              headerColor={ctx.color}
              contextId={ctx.id}
              yAxisRange={{ min: 5, max: 30 }}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={temperatura}
              unit="°C"
              icon={<FaTemperatureHigh size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "temperatura")}
            />
          </Grid>
          {renderNasaTemperature(ctx, msgs)}

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE SALINIDAD"
              label="Salinidad"
              borderColor="#2A9D8F"
              backgroundColor="#2A9D8F"
              messageFeed={msgs}
              metricKey="salinidad"
              headerColor={ctx.color}
              contextId={ctx.id}
              yAxisRange={{ min: 0, max: 34 }}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={salinidad}
              unit="PSU"
              icon={<FaTint size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "salinidad")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE CLOROFILA"
              label="Clorofila"
              borderColor="#2A9D8F"
              backgroundColor="#2A9D8F"
              messageFeed={msgs}
              metricKey="clorofila"
              headerColor={ctx.color}
              contextId={ctx.id}
              yAxisRange={{ min: 0, max: 40 }}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={clorofila}
              unit="µg/L"
              icon={<FaLeaf size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "clorofila")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE TURBIDEZ"
              label="Turbidez"
              borderColor="#6D597A"
              backgroundColor="#6D597A"
              messageFeed={msgs}
              metricKey="turbidez"
              headerColor={ctx.color}
              contextId={ctx.id}
              yAxisRange={{ min: 0, max: 150 }}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={turbidez}
              unit="NTU"
              icon={<FaCloud size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "turbidez")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE ALTURA SIGNIFICATIVA DE OLA"
              label="Altura sig. ola"
              borderColor="#4361EE"
              backgroundColor="#4361EE"
              messageFeed={msgs}
              metricKey="ola_altura_significativa"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={olaSig}
              unit="m"
              icon={<FaWaveSquare size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "ola_altura_significativa")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE ALTURA MÁXIMA DE OLA"
              label="Altura máx. ola"
              borderColor="#8E24AA"
              backgroundColor="#8E24AA"
              messageFeed={msgs}
              metricKey="ola_altura_maxima"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={olaMax}
              unit="m"
              icon={<FaWaveSquare size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "ola_altura_maxima")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE PERÍODO DE PICO"
              label="Período pico"
              borderColor="#5E60CE"
              backgroundColor="#5E60CE"
              messageFeed={msgs}
              metricKey="ola_periodo_pico"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={olaPeriodo}
              unit="s"
              icon={<FaWaveSquare size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "ola_periodo_pico")}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderStandardContext = (ctx: typeof contexts[number]) => {
    const msgs = getFeed(ctx.id);
    const lastTemp = getLatestValue(msgs, "temperatura");
    const temperatura = formatValue(lastTemp, 2);
    const isExo1 = ctx.id === "flowers_island_exo1_context";

    if (isExo1) {
      const lastSalinidad = getLatestValue(msgs, "salinidad");
      const lastTurbidez = getLatestValue(msgs, "turbidez");
      const lastClorofila = getLatestValue(msgs, "clorofila");
      const lastPh = getLatestValue(msgs, "ph");

      const salinidad = formatValue(lastSalinidad, 2);
      const turbidez = formatValue(lastTurbidez, 2);
      const clorofila = formatValue(lastClorofila, 2);
      const ph = formatValue(lastPh, 2);

      return (
        <Card key={ctx.id} title={`MONITOREO - ${ctx.label}`} headerColor={ctx.color}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <DashboardIcon title="MONITOREO" />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item {...chartGridProps}>
              <RealTimeGraph
                title="EVOLUCIÓN DE TEMPERATURA DEL AGUA"
                label="Temperatura"
                borderColor="#18265a"
                backgroundColor="#18265a"
                messageFeed={msgs}
                metricKey="temperatura"
                headerColor={ctx.color}
                contextId={ctx.id}
                yAxisRange={{ min: 5, max: 30 }}
              />
            </Grid>
            <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={temperatura}
              unit="°C"
              icon={<FaTemperatureHigh size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "temperatura")}
            />
            </Grid>
            {renderNasaTemperature(ctx, msgs)}

            <Grid item {...chartGridProps}>
              <RealTimeGraph
                title="EVOLUCIÓN DE SALINIDAD"
                label="Salinidad"
                borderColor="#2A9D8F"
                backgroundColor="#2A9D8F"
                messageFeed={msgs}
                metricKey="salinidad"
                headerColor={ctx.color}
                contextId={ctx.id}
                yAxisRange={{ min: 0, max: 34 }}
              />
            </Grid>
            <Grid item {...cardGridProps}>
              <IconWithValue
                title="ACTUAL"
                value={salinidad}
                unit="PSU"
                icon={<FaTint size={50} />}
                headerColor={ctx.color}
                recvTime={getLatestRecvTimeFor(msgs, "salinidad")}
              />
            </Grid>

            <Grid item {...chartGridProps}>
              <RealTimeGraph
                title="EVOLUCIÓN DE TURBIDEZ"
                label="Turbidez"
                borderColor="#6D597A"
                backgroundColor="#6D597A"
                messageFeed={msgs}
                metricKey="turbidez"
                headerColor={ctx.color}
                contextId={ctx.id}
                yAxisRange={{ min: 0, max: 150 }}
              />
            </Grid>
            <Grid item {...cardGridProps}>
              <IconWithValue
                title="ACTUAL"
                value={turbidez}
                unit="NTU"
                icon={<FaCloud size={50} />}
                headerColor={ctx.color}
                recvTime={getLatestRecvTimeFor(msgs, "turbidez")}
              />
            </Grid>

            <Grid item {...chartGridProps}>
              <RealTimeGraph
                title="EVOLUCIÓN DE CLOROFILA"
                label="Clorofila"
                borderColor="#2A9D8F"
                backgroundColor="#2A9D8F"
                messageFeed={msgs}
                metricKey="clorofila"
                headerColor={ctx.color}
                contextId={ctx.id}
                yAxisRange={{ min: 0, max: 40 }}
              />
            </Grid>
            <Grid item {...cardGridProps}>
              <IconWithValue
                title="ACTUAL"
                value={clorofila}
                unit="µg/L"
                icon={<FaLeaf size={50} />}
                headerColor={ctx.color}
                recvTime={getLatestRecvTimeFor(msgs, "clorofila")}
              />
            </Grid>

            <Grid item {...chartGridProps}>
              <RealTimeGraph
                title="EVOLUCIÓN DE pH"
                label="pH"
                borderColor="#8AB17D"
                backgroundColor="#8AB17D"
                messageFeed={msgs}
                metricKey="ph"
                headerColor={ctx.color}
                contextId={ctx.id}
                yAxisRange={{ min: 6, max: 9 }}
              />
            </Grid>
            <Grid item {...cardGridProps}>
              <IconWithValue
                title="ACTUAL"
                value={ph}
                unit="pH"
                icon={<FaFlask size={50} />}
                headerColor={ctx.color}
                recvTime={getLatestRecvTimeFor(msgs, "ph")}
              />
            </Grid>
          </Grid>
        </Card>
      );
    }

    const lastCond = getLatestValue(msgs, "conductividad");
    const lastVolt = getLatestValue(msgs, "voltaje_device");
    const conductividad =
      lastCond !== null && lastCond !== undefined ? Math.round(Number(lastCond)) : null;
    const voltaje = formatValue(lastVolt, 2);

    return (
      <Card key={ctx.id} title={`MONITOREO - ${ctx.label}`} headerColor={ctx.color}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <DashboardIcon title="MONITOREO" />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE TEMPERATURA DEL AGUA"
              label="Temperatura"
              borderColor="#18265a"
              backgroundColor="#18265a"
              messageFeed={msgs}
              metricKey="temperatura"
              headerColor={ctx.color}
              contextId={ctx.id}
              yAxisRange={{ min: 5, max: 30 }}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={temperatura}
              unit="°C"
              icon={<FaTemperatureHigh size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "temperatura")}
            />
          </Grid>
          {renderNasaTemperature(ctx, msgs)}

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE CONDUCTIVIDAD DEL AGUA"
              label="Conductividad"
              borderColor="#C21292"
              backgroundColor="#C21292"
              messageFeed={msgs}
              metricKey="conductividad"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={conductividad}
              unit="µS/cm"
              icon={<FaWater size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "conductividad")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DEL VOLTAJE DEL DISPOSITIVO"
              label="Voltaje"
              borderColor="#FFA732"
              backgroundColor="#FFA732"
              messageFeed={msgs}
              metricKey="voltaje_device"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={voltaje}
              unit="V"
              icon={getBatteryIconByVoltage(voltaje)}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "voltaje_device")}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderBuoyContext = (ctx: typeof contexts[number]) => {
    const msgs = getFeed(ctx.id);
    const lastTempAgua = getLatestValue(msgs, "temperatura_agua");
    const lastWindSpeed = getLatestValue(msgs, "velocidad_viento");
    const lastWaveSig = getLatestValue(msgs, "ola_altura_significativa");
    const lastWaveMax = getLatestValue(msgs, "ola_altura_maxima");
    const lastWavePeak = getLatestValue(msgs, "ola_periodo_pico");
    const lastWaveDir = getLatestValue(msgs, "ola_direccion_media");
    const lastWindDir = getLatestValue(msgs, "direccion_viento");
    const lastCurrentSpeed = getLatestValue(msgs, "velocidad_corriente");
    const lastCurrentDir = getLatestValue(msgs, "direccion_corriente");

    const temperaturaAgua = formatValue(lastTempAgua, 2);
    const velocidadViento = formatValue(lastWindSpeed, 2);
    const alturaOlaSign = formatValue(lastWaveSig, 2);
    const alturaOlaMax = formatValue(lastWaveMax, 2);
    const periodoPico = formatValue(lastWavePeak, 2);
    const direccionOla = formatValue(lastWaveDir, 0);
    const direccionViento = formatValue(lastWindDir, 0);
    const velocidadCorriente = formatValue(lastCurrentSpeed, 2);
    const direccionCorriente = formatValue(lastCurrentDir, 0);

    return (
      <Card key={ctx.id} title={`MONITOREO - ${ctx.label}`} headerColor={ctx.color}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <DashboardIcon title="MONITOREO" />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE TEMPERATURA DEL AGUA"
              label="Temperatura agua"
              borderColor="#18265a"
              backgroundColor="#18265a"
              messageFeed={msgs}
              metricKey="temperatura_agua"
              headerColor={ctx.color}
              contextId={ctx.id}
              yAxisRange={{ min: 5, max: 30 }}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={temperaturaAgua}
              unit="°C"
              icon={<FaTemperatureHigh size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "temperatura_agua")}
            />
          </Grid>
          {renderNasaTemperature(ctx, msgs)}

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE VELOCIDAD DEL VIENTO"
              label="Velocidad viento"
              borderColor="#0B7285"
              backgroundColor="#0B7285"
              messageFeed={msgs}
              metricKey="velocidad_viento"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={velocidadViento}
              unit="m/s"
              icon={<FaWind size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "velocidad_viento")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE DIRECCIÓN DEL VIENTO"
              label="Dirección viento"
              borderColor="#6C63FF"
              backgroundColor="#6C63FF"
              messageFeed={msgs}
              metricKey="direccion_viento"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={direccionViento}
              unit="°"
              icon={<FaWind size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "direccion_viento")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE ALTURA SIGNIFICATIVA DE OLA"
              label="Altura sig. ola"
              borderColor="#4361EE"
              backgroundColor="#4361EE"
              messageFeed={msgs}
              metricKey="ola_altura_significativa"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={alturaOlaSign}
              unit="m"
              icon={<FaArrowsAltV size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "ola_altura_significativa")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE ALTURA MÁXIMA DE OLA"
              label="Altura máx. ola"
              borderColor="#14213D"
              backgroundColor="#14213D"
              messageFeed={msgs}
              metricKey="ola_altura_maxima"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={alturaOlaMax}
              unit="m"
              icon={<FaArrowsAltV size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "ola_altura_maxima")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE PERÍODO PICO"
              label="Período pico"
              borderColor="#DD6B20"
              backgroundColor="#DD6B20"
              messageFeed={msgs}
              metricKey="ola_periodo_pico"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={periodoPico}
              unit="s"
              icon={<FaArrowsAltV size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "ola_periodo_pico")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE DIRECCIÓN MEDIA DE OLA"
              label="Dirección ola"
              borderColor="#2D6A4F"
              backgroundColor="#2D6A4F"
              messageFeed={msgs}
              metricKey="ola_direccion_media"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={direccionOla}
              unit="°"
              icon={<FaWaveSquare size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "ola_direccion_media")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE VELOCIDAD DE CORRIENTE"
              label="Velocidad corriente"
              borderColor="#0D9488"
              backgroundColor="#0D9488"
              messageFeed={msgs}
              metricKey="velocidad_corriente"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={velocidadCorriente}
              unit="m/s"
              icon={<FaWater size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "velocidad_corriente")}
            />
          </Grid>

          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE DIRECCIÓN DE CORRIENTE"
              label="Dirección corriente"
              borderColor="#5E60CE"
              backgroundColor="#5E60CE"
              messageFeed={msgs}
              metricKey="direccion_corriente"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={direccionCorriente}
              unit="°"
              icon={<FaWind size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "direccion_corriente")}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderSohmaContext = (ctx: typeof contexts[number]) => {
    const msgs = getFeed(ctx.id);
    const lastNivelMar = getLatestValue(msgs, "nivel_mar");
    const lastTempAgua = getLatestValue(msgs, "temperatura_agua");
    const nivelMar = formatValue(lastNivelMar, 3);
    const temperaturaAgua = formatValue(lastTempAgua, 2);
    const hasTemperatura = temperaturaAgua !== null;

    return (
      <Card key={ctx.id} title={`MONITOREO - ${ctx.label}`} headerColor={ctx.color}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <DashboardIcon title="MONITOREO" />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          {hasTemperatura && (
            <>
              <Grid item {...chartGridProps}>
                <RealTimeGraph
                  title="EVOLUCIÓN DE TEMPERATURA DEL AGUA"
                  label="Temperatura agua"
                  borderColor="#FF7043"
                  backgroundColor="#FF7043"
                  messageFeed={msgs}
                  metricKey="temperatura_agua"
                  headerColor={ctx.color}
                  contextId={ctx.id}
                />
              </Grid>
              <Grid item {...cardGridProps}>
                <IconWithValue
                  title="ACTUAL"
                  value={temperaturaAgua}
                  unit="°C"
                  icon={<FaTemperatureHigh size={50} />}
                  headerColor={ctx.color}
                  recvTime={getLatestRecvTimeFor(msgs, "temperatura_agua")}
                />
              </Grid>
            </>
          )}
          {renderNasaTemperature(ctx, msgs)}
          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DEL NIVEL DEL MAR"
              label="Nivel del mar"
              borderColor="#4A90E2"
              backgroundColor="#4A90E2"
              messageFeed={msgs}
              metricKey="nivel_mar"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={nivelMar}
              unit="m"
              icon={<FaWaveSquare size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "nivel_mar")}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderAnpMareografoContext = (ctx: typeof contexts[number]) => {
    const msgs = getFeed(ctx.id);
    const lastNivelMar = getLatestValue(msgs, "nivel_mar");
    const nivelMar = formatValue(lastNivelMar, 3);

    return (
      <Card key={ctx.id} title={`MONITOREO - ${ctx.label}`} headerColor={ctx.color}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <DashboardIcon title="MONITOREO" />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          {renderNasaTemperature(ctx, msgs)}
          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DEL NIVEL DEL MAR"
              label="Nivel del mar"
              borderColor="#0B7285"
              backgroundColor="#0B7285"
              messageFeed={msgs}
              metricKey="nivel_mar"
              headerColor={ctx.color}
              contextId={ctx.id}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={nivelMar}
              unit="m"
              icon={<FaWaveSquare size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "nivel_mar")}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderPlayaContext = (ctx: typeof contexts[number]) => {
    const msgs = getFeed(ctx.id);
    const lastTempAgua = getLatestValue(msgs, "temperatura_agua");
    const lastSalinidad = getLatestValue(msgs, "salinidad");
    const temperaturaAgua = formatValue(lastTempAgua, 2);
    const salinidad = formatValue(lastSalinidad, 2);
    const hasTemp = temperaturaAgua !== null;

    return (
      <Card key={ctx.id} title={`MONITOREO - ${ctx.label}`} headerColor={ctx.color}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <DashboardIcon title="MONITOREO" />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          {hasTemp && (
            <>
              <Grid item {...chartGridProps}>
                <RealTimeGraph
                  title="EVOLUCIÓN DE TEMPERATURA DEL AGUA"
                  label="Temperatura agua"
                  borderColor="#5E60CE"
                  backgroundColor="#5E60CE"
                  messageFeed={msgs}
                  metricKey="temperatura_agua"
                  headerColor={ctx.color}
                  contextId={ctx.id}
                />
              </Grid>
              <Grid item {...cardGridProps}>
                <IconWithValue
                  title="ACTUAL"
                  value={temperaturaAgua}
                  unit="°C"
                  icon={<FaTemperatureHigh size={50} />}
                  headerColor={ctx.color}
                  recvTime={getLatestRecvTimeFor(msgs, "temperatura_agua")}
                />
              </Grid>
            </>
          )}
          {renderNasaTemperature(ctx, msgs)}
          <Grid item {...chartGridProps}>
            <RealTimeGraph
              title="EVOLUCIÓN DE SALINIDAD"
              label="Salinidad"
              borderColor="#0B7285"
              backgroundColor="#0B7285"
              messageFeed={msgs}
              metricKey="salinidad"
              headerColor={ctx.color}
              contextId={ctx.id}
              yAxisRange={{ min: 0, max: 34 }}
            />
          </Grid>
          <Grid item {...cardGridProps}>
            <IconWithValue
              title="ACTUAL"
              value={salinidad}
              unit="PSU"
              icon={<FaTint size={50} />}
              headerColor={ctx.color}
              recvTime={getLatestRecvTimeFor(msgs, "salinidad")}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, px: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Datos de la Intendencia de Montevideo, analizado en el laboratorio del Faro de Punta Carretas
          </Typography>
        </Box>
      </Card>
    );
  };

  return (
    <Box
      component="section"
      sx={{
        pt: 3,
        pb: 4,
        px: { xs: 2, sm: 3, lg: 6 },
      }}
    >
      <Box sx={{ maxWidth: 1360, mx: "auto", mb: 3 }}>
        <Card title="Buscar contextos">
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Escribe para filtrar (ej: ANP, isla, boya, playas...)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ color: "text.secondary", fontSize: 14, lineHeight: 1.5 }}>
                  Usa el buscador para reducir la lista de tarjetas. Ejemplos:
                  <strong> isla</strong> muestra todas las estaciones de Isla de Flores,
                  <strong> ANP</strong> lista solo los mareógrafos de la Administración Nacional de Puertos,
                  <strong> SOHMA</strong> lista solo los mareógrafos del Servicio de Oceanografía, Hidrografía y Meteorología de la Armada,
                  <strong> playa</strong> lista los monitoreos de playas de la Intendencia de Montevideo.
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
                  {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <input
                      type="checkbox"
                      id="onlyFresh"
                      checked={onlyFresh}
                      onChange={(e) => setOnlyFresh(e.target.checked)}
                    />
                    <label htmlFor="onlyFresh" style={{ cursor: "pointer", color: "inherit" }}>
                      Mostrar solo contextos con datos en la tarjeta ACTUAL
                    </label>
                  </Box> */}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Box>

      <Box
        sx={{
          maxWidth: 1360,
          mx: "auto",
          display: "grid",
          gap: 3,
        }}
      >
        {filteredContexts.map((ctx) => {
          if (ctx.id === "montevideo_boya_context") {
            return renderBuoyContext(ctx);
          }
          if (["sohma_la_paloma_mareografica_context", "sohma_punta_lobos_mareografica_context"].includes(ctx.id)) {
            return renderSohmaContext(ctx);
          }
          if (ctx.id.startsWith("anp_") && ctx.id.includes("mareografo_context")) {
            return renderAnpMareografoContext(ctx);
          }
          if (ctx.id === "simcosta_rs4_context") {
            return renderSimcostaContext(ctx);
          }
          if (ctx.id.startsWith("playa_mvd_")) {
            return renderPlayaContext(ctx);
          }
          return renderStandardContext(ctx);
        })}
      </Box>
    </Box>
  );
};

export default DevicesPage;
